import { motion } from "framer-motion";
import {
    Layers,
    SquareStack,
    Ruler,
    Cylinder,
    Component,
    Circle,
    CircleDot,
    Shield,
    Box,
    Zap,
    ShieldHalf,
    Anchor,
    MoveHorizontal,
    Package,
    Cog,
    PencilRuler,
    FileDown,
} from "lucide-react";
import Reveal from "../Reveal";
import SectionHead from "../SectionHead";

const PHOTOS = [
    { src: "/products/frp-sheet-green.jpg", label: "FRP / GFRP Sheets" },
    { src: "/products/frp-sheet-yellow.jpg", label: "Epoxy Fiberglass Sheets" },
    { src: "/products/frp-ring.jpg", label: "FRP Rings" },
    { src: "/products/frp-nut.jpg", label: "FRP Nuts & Washers" },
    { src: "/products/frp-cylinder.jpg", label: "FRP Cylinders" },
    { src: "/products/frp-profile.jpg", label: "FRP Profiles & Channels" },
    { src: "/products/frp-box.jpg", label: "FRP Box Sections" },
    { src: "/products/frp-angle.jpg", label: "FRP Angles & Bosses" },
    { src: "/products/frp-plate.jpg", label: "FRP Plates & I-Sections" },
];

const GROUPS = [
    {
        heading: "Electrical Insulation Products",
        items: [
            { icon: Layers, name: "FRP/GFRP Sheets" },
            { icon: SquareStack, name: "Epoxy Fiberglass Sheets" },
            { icon: Ruler, name: "FRP Strips" },
            { icon: Cylinder, name: "FRP Rods" },
            { icon: Component, name: "FRP Profiles" },
            { icon: Circle, name: "FRP Washers" },
            { icon: CircleDot, name: "FRP Rings" },
            { icon: Shield, name: "Insulation Components" },
        ],
    },
    {
        heading: "Customized FRP Components",
        items: [
            { icon: Box, name: "FRP Epoxy Boxes" },
            { icon: Zap, name: "Transformer Insulation Components" },
            { icon: ShieldHalf, name: "Electrical Barriers" },
            { icon: Anchor, name: "Supports" },
            { icon: MoveHorizontal, name: "Spacers" },
            { icon: Package, name: "Blocks" },
            { icon: Cog, name: "Custom-Machined Components" },
            { icon: PencilRuler, name: "Drawing-Based Components" },
        ],
    },
];

export const Products = () => (
    <section id="products" className="py-24 md:py-32 scroll-mt-20">
        <div className="shell">
            <SectionHead
                num="09"
                overline="Product Portfolio"
                title="Sheets, boxes, rods & everything between"
            />

            <Reveal>
                <div
                    className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-14"
                    data-testid="product-photo-gallery"
                >
                    {PHOTOS.map((photo, i) => (
                        <motion.figure
                            key={photo.label}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.5, delay: i * 0.07 }}
                            data-testid={`product-photo-${i + 1}`}
                            className="group relative border border-zinc-800 overflow-hidden bg-[#0e0e11] aspect-square"
                        >
                            <img
                                src={photo.src}
                                alt={photo.label}
                                className="absolute inset-0 w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-[1.05] transition-all duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#09090b]/85 via-transparent to-transparent" />
                            <figcaption className="absolute bottom-0 inset-x-0 p-3">
                                <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-safety">
                                    ACTUAL PRODUCT
                                </span>
                                <div className="font-display uppercase font-medium tracking-wide text-xs md:text-sm text-zinc-100 mt-0.5 leading-snug">
                                    {photo.label}
                                </div>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </Reveal>

            {GROUPS.map((group, gi) => (
                <div key={group.heading} className={gi === 0 ? "mb-12" : ""}>
                    <Reveal>
                        <div className="flex items-center gap-4 mb-6">
                            <h3 className="font-display uppercase font-semibold text-lg md:text-xl text-zinc-50">
                                {group.heading}
                            </h3>
                            <span className="h-px flex-1 bg-zinc-800" />
                            <span className="font-mono text-[10px] text-zinc-600 tracking-[0.2em]">
                                {String(group.items.length).padStart(2, "0")} TYPES
                            </span>
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
                        {group.items.map((item, i) => (
                            <motion.div
                                key={item.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
                                data-testid={`product-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="group bg-[#0e0e11] hover:bg-panel transition-colors duration-300 p-5 md:p-6 min-h-[120px] md:min-h-[140px] flex flex-col justify-between"
                            >
                                <item.icon
                                    size={22}
                                    strokeWidth={1.5}
                                    className="text-zinc-600 group-hover:text-safety transition-colors"
                                />
                                <span className="font-display uppercase font-medium tracking-wide text-sm md:text-base text-zinc-200 group-hover:text-zinc-50 leading-snug">
                                    {item.name}
                                </span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            ))}

            <Reveal>
                <div
                    className="border border-zinc-800 bg-panel/60 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6"
                    data-testid="brochure-cta-bar"
                >
                    <div className="flex items-center gap-5">
                        <FileDown size={30} strokeWidth={1.5} className="text-safety shrink-0" />
                        <div>
                            <div className="font-display uppercase font-semibold tracking-wide text-lg md:text-xl text-zinc-50">
                                Capability Profile — PDF Brochure
                            </div>
                            <p className="text-sm text-zinc-400 mt-1 leading-relaxed max-w-lg">
                                The complete company profile: capabilities, process, machinery, quality
                                systems and contact details. Share it with your purchase or design team.
                            </p>
                        </div>
                    </div>
                    <a
                        href="/naagarjun-power-fiber-brochure.pdf"
                        download="Naagarjun-Power-Fiber-Capability-Profile.pdf"
                        data-testid="brochure-download"
                        className="btn-solid shrink-0 justify-center"
                    >
                        Download PDF <FileDown size={16} />
                    </a>
                </div>
            </Reveal>
        </div>
    </section>
);

export default Products;